import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

import { ReactComponent as RadioOff } from "../../../assets/images/radio-off.svg";
import { ReactComponent as RadioOn } from "../../../assets/images/radio-on.svg";
import { ReactComponent as DDxEdit } from "../../../assets/images/ddx-edit.svg";

interface PatientGenderInputProps {
  gender: "M" | "F" | null;
  /** Called with the picked value; never with an updater. */
  setGender: (gender: "M" | "F" | null) => void;
  disabled?: boolean;
  onPressEdit: () => void;
  onPressSend?: (gender?:"M" | "F" | null ) => void;
}

const PatientGenderInput: React.FC<PatientGenderInputProps> = ({
  gender,
  setGender,
  disabled = false,
  onPressEdit,
  onPressSend = () => {},
}) => {
  const [value, setValue] = useState<"M" | "F" | null>(null); //for animation
  const [tempValue, setTempValue] = useState<"M"|"F"|null>(null);

  const [isEditMode, setIsEditMode] = useState<boolean>(false);

  useEffect(() => {
    setValue(gender);
  }, [gender]);

  const renderState = () => {
    switch (gender) {
      case "M":
      case "F":
        return (
          <motion.div
            key={`answered-${gender}`}
            initial={{
              opacity: 0,
              scale: 0.5,
              transformOrigin: "top right",
            }}
            animate={{ opacity: 1, scale: 1, transformOrigin: "top right" }}
            exit={{ opacity: 0, scale: 0.5, transformOrigin: "top right" }}
            transition={{ duration: 0.2 }}
            className="flex flex-row items-end gap-2.5 my-7 justify-end"
          >
            <button
              onClick={() => {
                onPressEdit();
                setIsEditMode(true);
                setTempValue(gender);
                setValue(null);
                setGender(null);
              }}
              className="mb-3"
            >
              <DDxEdit className="min-h-6 min-w-6" />
            </button>
            <div className="bg-white rounded-l-[18px] rounded-tr-[18px] shadow-[0px_0px_12px_0px_#A238FF0D] p-3.5 flex flex-col gap-4">
              <p className="text-sm leading-5 text-gray-800 font-normal">
                <span className="font-semibold">Patient&apos;s Gender: </span>
                {gender === "M" ? "Male" : "Female"}
              </p>
            </div>
          </motion.div>
        );
      case null:
        return (
          <motion.div
            key={"unanswered"}
            initial={{
              opacity: 0,
              scale: 0.5,
              transformOrigin: "top right",
            }}
            animate={{ opacity: 1, scale: 1, transformOrigin: "top right" }}
            exit={{ opacity: 0, scale: 0.5, transformOrigin: "top right" }}
            transition={{ duration: 0.2 }}
            className="bg-white rounded-l-[18px] rounded-tr-[18px] ml-7 my-7 shadow-[0px_0px_12px_0px_#A238FF0D] p-3.5 flex flex-col gap-4"
          >
            <p className="font-semibold text-gray-800 text-left text-base leading-6">
              Select Patient&apos;s Gender
            </p>
            <div className="w-full flex flex-row items-center gap-4">
              <button
                onClick={() => {
                  setValue("M");
                  setTimeout(() => {
                    setGender("M");
                    if (isEditMode && tempValue !== "M") {
                      onPressSend("M");
                      setIsEditMode(false);
                      setTempValue(null);
                    }
                  }, 400);
                }}
                className={`flex flex-row flex-1 items-center justify-between p-4 border-[1px] rounded-2xl ${
                  value === "M"
                    ? `border-primary bg-[#4B4AD51A]`
                    : `border-[#DCE4E8] bg-white`
                }`}
              >
                <p className="text-sm leading-5 font-semibold text-[#5A6774]">
                  Male
                </p>
                {value === "M" ? <RadioOn /> : <RadioOff />}
              </button>
              <button
                onClick={() => {
                  setValue("F");
                  setTimeout(() => {
                    setGender("F");
                    if (isEditMode && tempValue !== "F") {
                      onPressSend("F");
                      setIsEditMode(false);
                      setTempValue(null);
                    }
                  }, 400);
                }}
                className={`flex flex-row flex-1 items-center justify-between p-4 border-[1px] rounded-2xl ${
                  value === "F"
                    ? `border-primary bg-[#4B4AD51A]`
                    : `border-[#DCE4E8] bg-white`
                }`}
              >
                <p className="text-sm leading-5 font-semibold text-[#5A6774]">
                  Female
                </p>
                {value === "F" ? <RadioOn /> : <RadioOff />}
              </button>
            </div>
          </motion.div>
        );
      default:
        return <></>;
    }
  };

  return (
    <div className={`${disabled && "opacity-50 pointer-events-none"}`}>
      <AnimatePresence mode="wait">{renderState()}</AnimatePresence>
    </div>
  );
};

export default PatientGenderInput;
